/*
 * Copyright (c) 2022. Nedaware SL
 */

import {Inject, Injectable, InjectionToken, Type} from '@angular/core';
import {BasicEntityInterface} from '../basic-entity-interface/basic-entity-interface';
import {Resource} from '../../api/resource';
import {basicEntityDetailsMetadataKey, basicEntityMapMetadataKey, glob_GetBasicEntityModelList, objectMapMetadataKey} from '../annotations';
import 'reflect-metadata';
import {BasicEntityInterfaceDescription, ManagerCachingStrategy, ObjectPropertyMap} from "../basic-entity-interface/mapping-external";
import {EntityManager} from "../entity-manager/entity-manager";
import {ApiModuleFactory} from "../../api/api-module-factory.service";
import {TokenService} from "../../api/session/token.service";
import {InternalObjectPropertyMap} from "../basic-entity-interface/mapping-internal";
import {CasterFactory} from "../basic-entity-interface/caster-factory";
import {JsonLdInterface} from "../../api/entity-interface";
import {Uri} from "../../api/uri";

/**
 * Use this token to inject the paths for the InterfaceProviderService to be able to find the paths
 * for the models
 */
export const INTERFACE_PROVIDER_PATHS =
    new InjectionToken<{ path: string, component: Type<any>, model: Type<Resource> }>('BasicEntityTable-InterfaceProvider-Paths');

@Injectable({
    providedIn: "root"
})
export class InterfaceProviderService {
    private _createdInterfaces: Map<Type<any>, BasicEntityInterface<any>> = new Map();
    private _createdManagers: Map<Type<any>, EntityManager<any>> = new Map();
    private _objectPropertyMaps: Map<Type<any>, InternalObjectPropertyMap[]> = new Map();

    constructor(@Inject(INTERFACE_PROVIDER_PATHS) private _paths: { path: string, model: Type<Resource> }[],
                private _apiFactory: ApiModuleFactory, private _tokenSession: TokenService) {
        for (const model of glob_GetBasicEntityModelList()) {
            if (this.interfaceForModel(model).managerCaching === ManagerCachingStrategy.PrecacheOnStart) {
                this.managerForModel(model).preload();
            }
        }
    }

    public interfaceForModel<T extends Resource>(modelType: Type<T>): BasicEntityInterface<T> {
        if (!modelType) {
            throw new Error('No valid model provided in interfaceForModel. Provided: ' + modelType);
        }
        let interf = this._createdInterfaces.get(modelType);
        if (!interf) {
            const description = this.getDescriptionForModel(modelType);
            interf = new BasicEntityInterface<T>(description, this);
            this._createdInterfaces.set(modelType, interf);
            // IMPORTANT! Should be done after set, so it does not cause infinite recursion
            interf.createCasters();
        }
        return interf;
    }

    /**
     * Given a json-ld object, tries to find the right interface to parse it
     * using the IRI and the type. Returns undefined if no interface is found.
     * @param object
     */
    public interfaceForObject(object: JsonLdInterface): BasicEntityInterface<any> | undefined {
        for (const model of glob_GetBasicEntityModelList()) {
            const interf = this.interfaceForModel(model);
            if (interf.serialiser.entityType === object["@type"]
                || Uri.uriMatchsPattern(object["@id"], interf.uriPattern)) {
                return interf;
            }
        }
        return undefined;
    }

    /**
     * Given a resource, it gets the manager for it
     * @param resource
     */
    public managerForResource<T extends Resource>(resource: T): EntityManager<T> {
        return this.managerForModel(resource.modelType as Type<T>);
    }

    /**
     * Given a resource, it gets the interface to manage it.
     * @param resource
     */
    public interfaceForResource<T extends Resource>(resource: T): BasicEntityInterface<T> {
        return this.interfaceForModel(resource.modelType as Type<T>);
    }

    public managerForModel<T extends Resource>(modelType: Type<T>): EntityManager<T> {
        if (!this._createdManagers.has(modelType)) {
            const interf = this.interfaceForModel(modelType);
            const loader = this._apiFactory.createEntityLoader(interf);
            this._createdManagers.set(modelType, new EntityManager(loader, this._tokenSession));
        }
        return this._createdManagers.get(modelType);
    }

    private getDescriptionForModel<T extends Resource>(modelType: Type<T>): BasicEntityInterfaceDescription<T> {
        const details = Reflect.getMetadata(basicEntityDetailsMetadataKey, modelType);
        // If no details are present in class metadata, we try to find them in INTERFACES
        if (!details) {
            const modelName = modelType.toString().substr(0, modelType.toString().indexOf('extends'));
            throw new Error(`Couldn't find interface for given model ${modelName}.`);
        } else {
            return {
                ...details,
                model: modelType,
                mappingModelToApi: this._getMapMetadata(modelType)
            };
        }
    }

    private _getMapMetadata(model): any {
        if (model === Resource) {
            return {};
        }
        const ownMetadata = Reflect.getOwnMetadata(basicEntityMapMetadataKey, model);
        return ownMetadata ?
            Object.assign(this._getMapMetadata(Object.getPrototypeOf(model)), ownMetadata) :
            this._getMapMetadata(Object.getPrototypeOf(model));
    }

    public getObjectProperties(model: Type<any>): InternalObjectPropertyMap[] {
        if (!this._objectPropertyMaps.has(model)) {
            const maps = this._getObjectPropertyMaps(model);
            const properties = [];
            for (const [key, map] of Object.entries(maps)) {
                const prop = new InternalObjectPropertyMap(key, map);
                properties.push(prop);
            }

            // Important to set first, before intialising the casters,
            // to avoid infinite recursion
            this._objectPropertyMaps.set(model, properties);

            properties.forEach(prop =>
                prop.initialiseCasters(this,
                    CasterFactory.sDefaultCaster(this, prop),
                    CasterFactory.sStringCaster(this, prop)));
        }
        return this._objectPropertyMaps.get(model);
    }

    private _getObjectPropertyMaps(model: Type<any>): { [key: string]: ObjectPropertyMap } {
        if (model == null) {
            return {};
        }
        const ownMetadata = Reflect.getOwnMetadata(objectMapMetadataKey, model);
        return ownMetadata ?
            Object.assign(this._getObjectPropertyMaps(Object.getPrototypeOf(model)), ownMetadata) :
            this._getObjectPropertyMaps(Object.getPrototypeOf(model));
    }

    public allAcceptedInterfaces(modelOrParent: Function): BasicEntityInterface<Resource>[] {
        const interfaces: BasicEntityInterface<Resource>[] = [];
        for (const model of glob_GetBasicEntityModelList()) {
            if (this.isInstanceOf(model, modelOrParent)) {
                interfaces.push(this.interfaceForModel(model));
            }
        }
        return interfaces;
    }

    private isInstanceOf(model: Type<Resource>, modelOrParent: Function) {
        if (model === modelOrParent) {
            return true;
        }
        const proto = Object.getPrototypeOf(model);
        if (proto) {
            return this.isInstanceOf(proto, modelOrParent);
        }
        return false;
    }

    public pathForModel<T extends Resource>(modelType: Type<T>): string {
        const path = this._paths.find(pathObject => pathObject.model === modelType);
        if (!path) {
            return null;
        }
        return path.path;
    }
}
